// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Checkbox, Grid, Group, Input, MultiSelect, Select, Stack, Textarea, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { ContactPoint, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  getPreferredContactMethods,
  getPreferredName,
  getPrimaryLanguage,
  getSleepConcerns,
  getVisitState,
  isTestPatient,
  setTestPatient,
} from '@polarissleep/shared';
import { IconCheck, IconSelector } from '@tabler/icons-react';
import { type JSX, useEffect, useMemo, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { FormCard } from '../../../components/FormCard';
import {
  PATIENT_EXTENSION_URLS,
  PRONOUN_OPTIONS,
  SEX_AT_BIRTH_OPTIONS,
  TITLE_OPTIONS,
  US_STATE_OPTIONS,
} from '../../../constants/patient-profile';
import { useRole } from '../../../hooks/useRole';
import { formatDateOnly, parseDateOnly } from '../../../utils/dateOnly';
import { getBirthSex, getPronouns, setBirthSex, setPronouns } from '../../../utils/patient-extensions';
import { dispatchPatientUpdated } from '../../../utils/patient-updates';
import {
  createHeightObservation,
  createWeightObservation,
  formatCmToImperial,
  getLatestHeightCm,
  getLatestWeightKg,
  kgToLbs,
  lbsToKg,
  parseHeightImperialToCm,
  parseWeightLbs,
} from '../../../utils/patient-vitals';
import { hasPermission } from '../../../utils/permissions';

const PHONE_REGEX = /^\+1 \(\d{3}\) \d{3}-\d{4}$/;
const ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const HEIGHT_REGEX = /^\d+\s*(?:ft|')\s*(?:\d+(?:\.\d+)?\s*(?:in|")?)?$/i;
const WEIGHT_REGEX = /^\d+(?:\.\d+)?\s*(?:lbs?|pounds?)?$/i;

function formatDisplayDate(value: Date | null): string {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(value);
}

function getTelecom(patient: Patient, system: ContactPoint['system']): string {
  return patient.telecom?.find((t) => t.system === system)?.value ?? '';
}

/** Pronoun labels, including any the patient typed under "Other", which the edit form does not offer. */
function getPronounLabels(patient: Patient): string {
  return (patient.extension ?? [])
    .filter((e) => e.url === PATIENT_EXTENSION_URLS.pronouns)
    .map((e) => {
      const coding = e.valueCodeableConcept?.coding?.[0];
      return PRONOUN_OPTIONS.find((o) => o.value === coding?.code)?.label ?? coding?.display ?? '';
    })
    .filter(Boolean)
    .join(' + ');
}

function setTelecom(patient: Patient, system: ContactPoint['system'], value: string): ContactPoint[] {
  const existing = patient.telecom?.filter((t) => t.system !== system) ?? [];
  return value ? [...existing, { system, value }] : existing;
}

interface FormValues {
  title: string;
  firstName: string;
  lastName: string;
  phone: string;
  dateOfBirth: Date | null;
  sexAtBirth: string;
  pronouns: string[];
  streetAddress: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  heightImperial: string;
  weightImperial: string;
  testPatient: boolean;
}

function buildInitialValues(patient: Patient, heightCm: number | undefined, weightKg: number | undefined): FormValues {
  const name = patient.name?.[0];
  const address = patient.address?.[0];
  return {
    title: name?.prefix?.[0] ?? '',
    firstName: name?.given?.[0] ?? '',
    lastName: name?.family ?? '',
    phone: getTelecom(patient, 'phone'),
    dateOfBirth: parseDateOnly(patient.birthDate),
    sexAtBirth: getBirthSex(patient),
    pronouns: getPronouns(patient),
    streetAddress: address?.line?.[0] ?? '',
    address2: address?.line?.[1] ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zipcode: address?.postalCode ?? '',
    heightImperial: formatCmToImperial(heightCm),
    weightImperial: weightKg !== undefined ? `${kgToLbs(weightKg)} lbs` : '',
    testPatient: isTestPatient(patient),
  };
}

export interface PersonalInformationSectionProps {
  patient: Patient;
  onSaved: (updated: Patient) => void;
}

export function PersonalInformationSection({ patient, onSaved }: PersonalInformationSectionProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canEdit = hasPermission({ role, permission: 'edit-patient-profile', isAdmin });

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [heightCm, setHeightCm] = useState<number | undefined>(undefined);
  const [weightKg, setWeightKg] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!patient.id) {
      return;
    }
    getLatestHeightCm(medplum, patient.id).then(setHeightCm).catch(console.error);
    getLatestWeightKg(medplum, patient.id).then(setWeightKg).catch(console.error);
  }, [medplum, patient.id]);

  const initialValues = useMemo(() => buildInitialValues(patient, heightCm, weightKg), [patient, heightCm, weightKg]);

  const form = useForm<FormValues>({
    initialValues,
    validate: {
      firstName: (v) => (v.trim() ? null : 'First name is required'),
      lastName: (v) => (v.trim() ? null : 'Last name is required'),
      phone: (v) => (!v || PHONE_REGEX.test(v) ? null : 'Enter a valid US phone number'),
      zipcode: (v) => (!v || ZIP_REGEX.test(v) ? null : 'Enter a 5-digit ZIP code'),
      heightImperial: (v) => (!v || HEIGHT_REGEX.test(v) ? null : `Enter height like 5'9"`),
      weightImperial: (v) => (!v || WEIGHT_REGEX.test(v) ? null : 'Enter weight in pounds'),
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: resync only when initialValues change; form and isEditing are read as latest-value guards
  useEffect(() => {
    if (!isEditing) {
      form.setValues(initialValues);
      form.resetDirty(initialValues);
    }
  }, [initialValues]);

  const email = getTelecom(patient, 'email');
  const preferredName = getPreferredName(patient) ?? '';
  const contactMethods = getPreferredContactMethods(patient).join(', ');
  const primaryLanguage = getPrimaryLanguage(patient) ?? '';
  const sleepConcerns = getSleepConcerns(patient).join('\n');
  const visitState = getVisitState(patient) ?? '';
  const dateOfBirthInput = form.getInputProps('dateOfBirth');

  const handleCancel = (): void => {
    form.reset();
    setIsEditing(false);
  };

  const handleSave = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      const birthSexOption = SEX_AT_BIRTH_OPTIONS.find((o) => o.value === values.sexAtBirth);

      const addressLines = [values.streetAddress, values.address2].filter(Boolean);

      let next: Patient = {
        ...patient,
        name: [
          {
            ...patient.name?.[0],
            prefix: values.title ? [values.title] : undefined,
            given: values.firstName ? [values.firstName] : undefined,
            family: values.lastName || undefined,
          },
          // Keeps the preferred (usual) name from intake, which this form does not edit.
          ...(patient.name?.slice(1) ?? []),
        ],
        birthDate: formatDateOnly(values.dateOfBirth),
        telecom: setTelecom(patient, 'phone', values.phone),
        address:
          addressLines.length || values.city || values.state || values.zipcode
            ? [
                {
                  ...patient.address?.[0],
                  use: patient.address?.[0]?.use ?? 'home',
                  type: patient.address?.[0]?.type ?? 'physical',
                  line: addressLines.length ? addressLines : undefined,
                  city: values.city || undefined,
                  state: values.state || undefined,
                  postalCode: values.zipcode || undefined,
                },
              ]
            : undefined,
      };

      if (birthSexOption) {
        next = setBirthSex(next, birthSexOption.value, birthSexOption.system, birthSexOption.label);
      } else {
        next = setBirthSex(next, '', '', '');
      }

      const pronounSelections = values.pronouns
        .map((code) => PRONOUN_OPTIONS.find((p) => p.value === code))
        .filter((o): o is (typeof PRONOUN_OPTIONS)[number] => Boolean(o))
        .map((o) => ({ code: o.value, display: o.label }));
      next = setPronouns(next, pronounSelections);
      next = setTestPatient(next, values.testPatient);

      const result = await medplum.updateResource(next);

      const newHeightCm = values.heightImperial ? parseHeightImperialToCm(values.heightImperial) : undefined;
      if (newHeightCm !== undefined && newHeightCm !== heightCm) {
        await createHeightObservation(medplum, result, newHeightCm);
        setHeightCm(newHeightCm);
      }

      const newWeightLbs = values.weightImperial ? parseWeightLbs(values.weightImperial) : undefined;
      const newWeightKg = newWeightLbs !== undefined ? lbsToKg(newWeightLbs) : undefined;
      if (newWeightKg !== undefined && newWeightKg !== weightKg) {
        await createWeightObservation(medplum, result, newWeightKg);
        setWeightKg(newWeightKg);
      }

      onSaved(result);
      dispatchPatientUpdated(result);
      setIsEditing(false);
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Profile updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  let action: JSX.Element | null = null;
  if (isEditing) {
    action = (
      <Group gap="xs">
        <Button size="xs" variant="default" onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="xs" onClick={() => handleSave()} loading={saving}>
          Save
        </Button>
      </Group>
    );
  } else if (canEdit) {
    action = (
      <Button size="xs" onClick={() => setIsEditing(true)}>
        Edit
      </Button>
    );
  }

  return (
    <FormCard title="Personal Information" isEditing={isEditing} action={action}>
      {isEditing ? (
        <Stack gap="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Select
                label="Title"
                placeholder="Select title"
                data={TITLE_OPTIONS}
                searchable
                clearable
                {...form.getInputProps('title')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="First Name" {...form.getInputProps('firstName')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Last Name" {...form.getInputProps('lastName')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Preferred Name" value={preferredName} readOnly disabled />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Email" value={email} readOnly disabled />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Input.Wrapper label="Phone Number" error={form.errors.phone}>
                <Input
                  component={IMaskInput}
                  mask="+1 (000) 000-0000"
                  placeholder="+1 (000) 000-0000"
                  {...form.getInputProps('phone')}
                />
              </Input.Wrapper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <DatePickerInput
                label="Date of Birth"
                placeholder="MM/DD/YYYY"
                maxDate={new Date()}
                clearable
                value={form.values.dateOfBirth}
                onChange={(value) => {
                  const nextValue = typeof value === 'string' ? parseDateOnly(value) : value;
                  form.setFieldValue('dateOfBirth', nextValue);
                }}
                onBlur={dateOfBirthInput.onBlur}
                error={dateOfBirthInput.error}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Select
                label="Sex at Birth"
                placeholder="Select"
                data={SEX_AT_BIRTH_OPTIONS}
                clearable
                {...form.getInputProps('sexAtBirth')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <MultiSelect
                label="Preferred Pronouns"
                placeholder="Select one or more"
                data={PRONOUN_OPTIONS}
                {...form.getInputProps('pronouns')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Preferred Contact Method" value={contactMethods} readOnly disabled />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Primary Language" value={primaryLanguage} readOnly disabled />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Visit State" value={visitState} readOnly disabled />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Street Address" {...form.getInputProps('streetAddress')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Address 2" {...form.getInputProps('address2')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="City" {...form.getInputProps('city')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Select
                label="State"
                placeholder="Select"
                data={US_STATE_OPTIONS}
                searchable
                clearable
                {...form.getInputProps('state')}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Zipcode" placeholder="00000" {...form.getInputProps('zipcode')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Height" placeholder={`5'9"`} {...form.getInputProps('heightImperial')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <TextInput label="Weight" placeholder="165 lbs" {...form.getInputProps('weightImperial')} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Input.Wrapper label="Test Patient">
                <Checkbox {...form.getInputProps('testPatient', { type: 'checkbox' })} />
              </Input.Wrapper>
            </Grid.Col>
            <Grid.Col span={12}>
              <Textarea label="Sleep Concerns" value={sleepConcerns} autosize readOnly disabled />
            </Grid.Col>
          </Grid>
        </Stack>
      ) : (
        <Grid>
          <ReadField label="Title" value={initialValues.title} span={6} />
          <ReadField label="First Name" value={initialValues.firstName} span={6} />
          <ReadField label="Last Name" value={initialValues.lastName} span={6} />
          <ReadField label="Preferred Name" value={preferredName} span={6} />
          <ReadField label="Email" value={email} span={6} />
          <ReadField label="Phone Number" value={initialValues.phone} span={6} />
          <ReadField label="Date of Birth" value={formatDisplayDate(initialValues.dateOfBirth)} span={6} />
          <ReadField
            label="Sex at Birth"
            value={SEX_AT_BIRTH_OPTIONS.find((o) => o.value === initialValues.sexAtBirth)?.label ?? ''}
            span={6}
            selectLike
          />
          <ReadField label="Preferred Pronouns" value={getPronounLabels(patient)} span={6} selectLike />
          <ReadField label="Preferred Contact Method" value={contactMethods} span={6} />
          <ReadField label="Primary Language" value={primaryLanguage} span={6} />
          <ReadField label="Visit State" value={visitState} span={6} />
          <ReadField label="Street Address" value={initialValues.streetAddress} span={6} />
          <ReadField label="Address 2" value={initialValues.address2} span={6} />
          <ReadField label="City" value={initialValues.city} span={6} />
          <ReadField label="State" value={initialValues.state} span={6} selectLike />
          <ReadField label="Zipcode" value={initialValues.zipcode} span={6} />
          <ReadField label="Height" value={initialValues.heightImperial} span={6} />
          <ReadField label="Weight" value={initialValues.weightImperial} span={6} />
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Input.Wrapper label="Test Patient">
              <Checkbox checked={initialValues.testPatient} readOnly disabled />
            </Input.Wrapper>
          </Grid.Col>
          <Grid.Col span={12}>
            <Textarea label="Sleep Concerns" value={sleepConcerns} autosize readOnly />
          </Grid.Col>
        </Grid>
      )}
    </FormCard>
  );
}

interface ReadFieldProps {
  label: string;
  value: string;
  span: number;
  /** When true, renders a chevron icon to mirror the Select affordance used in edit mode. */
  selectLike?: boolean;
}

function ReadField({ label, value, span, selectLike }: ReadFieldProps): JSX.Element {
  return (
    <Grid.Col span={{ base: 12, md: span }}>
      <TextInput
        label={label}
        value={value || ''}
        readOnly
        aria-label={label}
        rightSection={selectLike ? <IconSelector size={14} /> : undefined}
      />
    </Grid.Col>
  );
}
